import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSyncAlt } from 'react-icons/fa';
import { AdminNameCell, AdminRowActions, rowClickHandler } from '../admin/AdminRowCells';
import CollapsibleFilters from './CollapsibleFilters';
import { resolveOrderMobile } from './formatDisplay';
import {
  fetchDeliveryAgentOrders,
  filterDeliveryAgentOrders,
  getDeliveryAgentOrderSortValue,
  resolveDeliveryAgentOrderId,
  resolveJreatsMealType,
  resolveOrderId,
  updateDeliveryAgentOrderStatus,
} from './orderApi';
import {
  normalizeOnlineOrderStatusForFilter,
    onlineDisplayStatusToOrderStatusCode,
    resolveOrderStatusRaw,
} from './orderStatusApi';
import OrderHistoryDateFilters, {
  defaultOrderHistoryFromDate,
  defaultOrderHistoryToDate,
  orderMatchesHistoryDateRange,
} from './OrderHistoryDateFilters';
import OrderStatusFilterDropdown from './OrderStatusFilterDropdown';
import OrdersSortableTh from './OrdersSortableTh';
import OrdersTablePanel from './OrdersTablePanel';
import ToolbarRichSelect, { ORDER_VIEW_OPTIONS } from './ToolbarRichSelect';
import OrderItemsSummary from './ui/OrderItemsSummary';
import OrderStatusPill from './ui/OrderStatusPill';
import OrderBillingCell from './ui/OrderBillingCell';
import {
  getNextOrderStep,
  getNextStoreOrderStep,
  isTerminalStatus,
  storeOrderStatusChannel,
} from './ui/orderStatusFlow';
import { buildSortHandlers, sortList } from './tableSortUtils';
import { formatStoreOrderDateTime } from './storeOrderDateUtils';
import { isRestaurantStoreOrderSource } from './menuChannels';
import './css/Order.css';
import './css/Purchase.css';
import './css/TodayOrder.css';
import './css/Menu.css';
import './css/OrderStatusPill.css';

const ACTIVE_STATUS_OPTS = [
  { code: 'RDY', label: 'READY', matchNorm: ['READY', 'RDY'] },
  {
    code: 'OFD',
    label: 'OUT-FOR-DELIVERY',
    matchNorm: ['OUTFORDELIVERY', 'OFD', 'INTRANSIT', 'INTRANST'],
  },
];

const HISTORY_STATUS_OPTS = [
  { code: 'HIST_UNDL', label: 'UNDELIVERED', matchNorm: [] },
  { code: 'HIST_DLVD', label: 'DELIVERED', matchNorm: ['DELIVERED', 'DEL', 'PAID'] },
  { code: 'HIST_CANC', label: 'CANCELLED', matchNorm: ['CANCELLED', 'CAN'] },
];

function parseOrderCreatedDate(order) {
  const raw =
    order?.CreatedDate ??
    order?.createdDate ??
    order?.created_date ??
    order?.createdAt ??
    order?.created_at;
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const normalized = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(s) ? s.replace(' ', 'T') : s;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isOrderCreatedToday(order) {
  const d = parseOrderCreatedDate(order);
  if (!d) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function isOrderCreatedBeforeToday(order) {
  const d = parseOrderCreatedDate(order);
  if (!d) return false;
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const orderDay = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return orderDay < todayStart;
}

const UNDELIVERED_EXCLUDED_STATUS_NORMS = new Set([
  'DELIVERED',
  'DEL',
  'CANCELLED',
  'CAN',
  'COMPLETED',
  'COMPLETE',
  'CMP',
]);

function isUndeliveredOrderStatus(order) {
  return !UNDELIVERED_EXCLUDED_STATUS_NORMS.has(normalizeOrderStatusNorm(order));
}

/** Prior days — assigned but not delivered or cancelled. */
function orderMatchesUndeliveredHistoryFilter(order) {
  return isOrderCreatedBeforeToday(order) && isUndeliveredOrderStatus(order);
}

function isHistoryUndeliveredOpt(opt) {
  return opt?.code === 'HIST_UNDL';
}

function buildEmptyStatusSelections(opts) {
  return Object.fromEntries(opts.map((o) => [o.code, false]));
}

function normalizeOrderView(val) {
  if (val === 'history' || val === 'orderHistory' || val === 'delivered') return 'orderHistory';
  return 'orders';
}

function orderSourceToStoreChannel(orderSource) {
  if (isRestaurantStoreOrderSource(orderSource)) return 'restaurant';
  return 'myeats';
}

function orderSourceToRestaurantChannel(orderSource) {
  if (orderSource === 'JREATS') return 'jreats';
  return 'myeats';
}

function formatMoney(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toFixed(2)}`;
}

function formatDate(raw) {
  if (!raw) return '—';
  try {
    return formatStoreOrderDateTime(raw);
  } catch {
    return String(raw);
  }
}

function getFoodItems(order) {
  const raw =
    order.FoodItems ??
    order.food_items ??
    order.foodItems ??
    order.orderLineItems ??
    order.lineItems ??
    [];
  return Array.isArray(raw) ? raw : [];
}

function isStoreOrder(order) {
  return (order?.orderKind ?? order?.order_kind) === 'store';
}

function isTerminalDeliveryOrder(order) {
  if (isStoreOrder(order)) {
    return isTerminalStatus(order?.orderStatusCode ?? order?.orderStatus ?? order?.OrderStatus);
  }
  const code = onlineDisplayStatusToOrderStatusCode(resolveOrderStatusRaw(order));
  return code === 'DEL' || code === 'CAN' || code === 'CMP' || code === 'UND';
}

function normalizeOrderStatusNorm(order) {
  const raw =
    order?.orderStatusCode ??
    order?.orderStatus ??
    order?.Order_Status_Code ??
    order?.OrderStatus ??
    resolveOrderStatusRaw(order);
  return normalizeOnlineOrderStatusForFilter(raw);
}

function orderPassesStatusFilter(order, selections, opts) {
  const allSel = opts.every((o) => selections[o.code]);
  if (allSel) return true;
  const anySel = opts.some((o) => selections[o.code]);
  if (!anySel) return true;
  const norm = normalizeOrderStatusNorm(order);
  for (const opt of opts) {
    if (opt.matchNorm.includes(norm)) return !!selections[opt.code];
  }
  return false;
}

function orderPassesHistoryStatusFilter(order, selections, opts) {
  const allSel = opts.every((o) => selections[o.code]);
  if (allSel) {
    return isTerminalDeliveryOrder(order) || orderMatchesUndeliveredHistoryFilter(order);
  }
  const anySel = opts.some((o) => selections[o.code]);
  if (!anySel) {
    return isTerminalDeliveryOrder(order);
  }
  const norm = normalizeOrderStatusNorm(order);
  for (const opt of opts) {
    if (!selections[opt.code]) continue;
    if (isHistoryUndeliveredOpt(opt)) {
      if (orderMatchesUndeliveredHistoryFilter(order)) return true;
      continue;
    }
    if (opt.matchNorm.includes(norm)) return true;
  }
  return false;
}

const ACTIVE_READY_OFD_NORMS = new Set([
  'READY',
  'RDY',
  'OUTFORDELIVERY',
  'OFD',
]);

function isReadyOrOutForDeliveryOrder(order) {
  return ACTIVE_READY_OFD_NORMS.has(normalizeOrderStatusNorm(order));
}

function resolveStoreParty(order, isRestaurantChannel) {
  if (isRestaurantChannel) {
    return (
      order.restaurantName ??
      order.Restaurant_Name ??
      order.ReceiverName ??
      order.receiverName ??
      'Restaurant'
    );
  }
  return (
    order.CustomerName ??
    order.customerName ??
    order.receiverName ??
    order.ReceiverName ??
    'Customer'
  );
}

function resolveCustomerName(order) {
  return (
    order.CustomerName ||
    order.customerName ||
    order.customer_name ||
    order.ReceiverName ||
    order.receiverName ||
    'Customer'
  );
}

function resolveCustomerPhone(order) {
  return (
    order.receiverPhone ??
    order.ReceiverPhone ??
    order.customerPhone ??
    resolveOrderMobile(order) ??
    undefined
  );
}

function resolveCustomerEmail(order) {
  return order.customerEmail ?? order.CustomerEmail ?? order.email ?? undefined;
}

const COMPLETED_ORDER_NORMS = new Set(['COMPLETED', 'COMPLETE', 'CMP', 'DELIVERED', 'DEL', 'PAID']);
const CANCELLED_ORDER_NORMS = new Set(['CANCELLED', 'CANCELED', 'CAN', 'REJECTED', 'REJ', 'UNDELIVERED', 'UND']);

/** Maps an order status onto the order # pill colour (green done, red cancelled, grey otherwise). */
function orderPillStatus(rawStatus) {
  const norm = String(rawStatus ?? '').trim().toUpperCase().replace(/[\s_-]+/g, '');
  if (COMPLETED_ORDER_NORMS.has(norm)) return 'ACTIVE';
  if (CANCELLED_ORDER_NORMS.has(norm)) return 'INACTIVE';
  return rawStatus || '';
}

/** Plain clicks on the name link navigate with router state; modified clicks still open a new tab. */
function nameLinkClickCapture(open) {
  return (event) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (!event.target.closest('a.eats-admin-link')) return;
    event.preventDefault();
    open();
  };
}

function orderMatchesSearch(order, query, { isStore, isRestaurantChannel }) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const id = resolveDeliveryAgentOrderId(order) ?? resolveOrderId(order);
  const parts = [
    id,
    order.displayOrderRef,
    order.display_order_ref,
    order.SchoolName,
    order.organizationName,
    order.CustomerName,
    order.customerName,
    order.ReceiverName,
    order.receiverName,
    order.restaurantName,
    order.orderType,
    order.orderStatus,
    order.OrderStatus,
    ...getFoodItems(order).map(
      (item) =>
        item.FoodItemName ??
        item.foodItemName ??
        item.itemName ??
        item.food_item_name ??
        ''
    ),
  ];
  if (isStore && isRestaurantChannel) {
    parts.push(order.restaurantName, order.Restaurant_Name);
  }
  return parts.filter(Boolean).join(' ').toLowerCase().includes(q);
}

const DeliveryAgent = ({ orderSource = 'MYEATS', isStore = false }) => {
  const navigate = useNavigate();
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const userId = user?.userId ?? user?.id ?? user?.User_Id ?? user?.user_id ?? null;

  const [orderView, setOrderView] = useState('orders');
    const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'createdDate', direction: 'desc' });
  const [activeStatusSelections, setActiveStatusSelections] = useState(() =>
    buildEmptyStatusSelections(ACTIVE_STATUS_OPTS)
  );
  const [historyStatusSelections, setHistoryStatusSelections] = useState(() =>
    buildEmptyStatusSelections(HISTORY_STATUS_OPTS)
  );
  const [historyFromDate, setHistoryFromDate] = useState(defaultOrderHistoryFromDate);
  const [historyToDate, setHistoryToDate] = useState(defaultOrderHistoryToDate);

  const isHistoryView = orderView === 'orderHistory';
  const isJreats = !isStore && orderSource === 'JREATS';
  const isRestaurantChannel = isStore && orderSource === 'RESTAURANT';
  const storeChannel = orderSourceToStoreChannel(orderSource);
  const restaurantChannel = orderSourceToRestaurantChannel(orderSource);
  const showOrganizationColumn = !isStore && orderSource !== 'MYEATS';

  const statusFilterOpts = isHistoryView ? HISTORY_STATUS_OPTS : ACTIVE_STATUS_OPTS;
  const statusSelections = isHistoryView ? historyStatusSelections : activeStatusSelections;

  const { handleSortAsc, handleSortDesc } = useMemo(
    () => buildSortHandlers(setSort),
    []
  );

  const loadOrders = useCallback(async () => {
            if (!userId) {
      setOrders([]);
                return;
            }
    setLoading(true);
            try {
                const raw = await fetchDeliveryAgentOrders(userId);
      setOrders(filterDeliveryAgentOrders(raw, { orderSource }));
            } catch (error) {
                console.error(
                    'Error fetching delivery agent orders:',
                    error.response?.data || error.message
                );
                setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [userId, orderSource]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  useEffect(() => {
    setActiveStatusSelections(buildEmptyStatusSelections(ACTIVE_STATUS_OPTS));
    setHistoryStatusSelections(buildEmptyStatusSelections(HISTORY_STATUS_OPTS));
  }, [orderView, orderSource, isStore]);

  const toggleStatusFilter = (code) => {
    if (isHistoryView) {
      setHistoryStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    } else {
      setActiveStatusSelections((prev) => ({ ...prev, [code]: !prev[code] }));
    }
  };

  const filtered = useMemo(() => {
    return orders.filter((order) => {
      if (!isHistoryView) {
        if (isTerminalDeliveryOrder(order)) return false;
        if (!isReadyOrOutForDeliveryOrder(order)) return false;
        if (!isOrderCreatedToday(order)) return false;
        if (!orderPassesStatusFilter(order, statusSelections, statusFilterOpts)) {
          return false;
        }
        return orderMatchesSearch(order, search, { isStore, isRestaurantChannel });
      }

      if (!orderPassesHistoryStatusFilter(order, statusSelections, statusFilterOpts)) {
        return false;
      }
      if (!orderMatchesHistoryDateRange(order, historyFromDate, historyToDate)) {
        return false;
      }
      return orderMatchesSearch(order, search, { isStore, isRestaurantChannel });
    });
  }, [
    orders,
    isHistoryView,
    statusSelections,
    statusFilterOpts,
    search,
    isStore,
    isRestaurantChannel,
    historyFromDate,
    historyToDate,
  ]);

  const sortedOrders = useMemo(
    () => sortList(filtered, sort, getDeliveryAgentOrderSortValue),
    [filtered, sort]
  );

    const orderKey = (order) => resolveDeliveryAgentOrderId(order) ?? resolveOrderId(order);

  const orderDetailPath = (order) => {
    const id = orderKey(order);
    if (!id) return undefined;
    if (isStoreOrder(order)) return `/store-order/${id}`;
    return `/OrderDetails/${order.RestaurantId ?? order.restaurantId ?? order.restaurant_id ?? 0}/${id}`;
  };

  const openOrderDetail = (order) => {
    const path = orderDetailPath(order);
    if (!path) return;
    if (isStoreOrder(order)) {
      navigate(path, {
        state: {
          fromOrderType: storeChannel,
          fromOrderView: orderView,
        },
      });
      return;
    }
    navigate(path, {
      state: {
        fromOrderView: 'online',
        fromOrderHistory: isHistoryView,
        orderSource,
      },
        });
    };

  const handleAdvanceStatus = async (order) => {
    const id = orderKey(order);
    if (!id) return;
    const nextStep = isStoreOrder(order)
      ? getNextStoreOrderStep(storeChannel, order)
      : getNextOrderStep(restaurantChannel, resolveOrderStatusRaw(order));
    if (!nextStep) return;
    try {
      await updateDeliveryAgentOrderStatus(order, nextStep.nextStatus, userId);
      await loadOrders();
        } catch (error) {
            console.error(
                'Error updating order status:',
                error.response?.data || error.message
            );
      window.alert('Failed to update order status. Please try again.');
    }
  };

  const storeTableColSpan = 7;

  const restaurantTableColSpan = (() => {
    if (isJreats) return 13;
    return showOrganizationColumn ? 9 : 8;
  })();

  const renderNameCell = (order, name, label, rawStatus) => {
    const open = () => openOrderDetail(order);
    return (
      <td className="orders-name-cell" onClickCapture={nameLinkClickCapture(open)}>
        <AdminNameCell
          name={name}
          id={label}
          status={orderPillStatus(rawStatus)}
          to={orderDetailPath(order)}
        />
      </td>
    );
  };

  const renderActionsCell = (order, name, nextStep) => (
    <td className="action-cell menu-table-actions-cell">
      <div className="orders-row-actions">
        <AdminRowActions
          name={name}
          phone={resolveCustomerPhone(order)}
          email={resolveCustomerEmail(order)}
          onView={() => openOrderDetail(order)}
        />
        {nextStep ? (
          <button
            type="button"
            className="order-next-step-btn"
            title={nextStep.actionLabel}
            onClick={() => handleAdvanceStatus(order)}
          >
            {nextStep.actionLabel}
          </button>
        ) : null}
      </div>
    </td>
  );

  const renderStoreTable = () => (
    <table className="menu-table orders-name-table">
      <thead>
        <tr>
          <OrdersSortableTh
            label="Name"
            sortKey="orderId"
            activeSortKey={sort?.key}
            sortDirection={sort?.direction}
            onSortAsc={handleSortAsc}
            onSortDesc={handleSortDesc}
          />
          <OrdersSortableTh
            label="Items"
            sortKey="items"
            activeSortKey={sort?.key}
            sortDirection={sort?.direction}
            onSortAsc={handleSortAsc}
            onSortDesc={handleSortDesc}
          />
          <OrdersSortableTh
            label="Total"
            sortKey="total"
            activeSortKey={sort?.key}
            sortDirection={sort?.direction}
            onSortAsc={handleSortAsc}
            onSortDesc={handleSortDesc}
          />
          <OrdersSortableTh
            label="Status"
            sortKey="status"
            activeSortKey={sort?.key}
            sortDirection={sort?.direction}
            onSortAsc={handleSortAsc}
            onSortDesc={handleSortDesc}
          />
          <th>Type</th>
          <OrdersSortableTh
            label="Created On"
            sortKey="createdDate"
            activeSortKey={sort?.key}
            sortDirection={sort?.direction}
            onSortAsc={handleSortAsc}
            onSortDesc={handleSortDesc}
          />
          <th className="menu-table-actions-head">Actions</th>
        </tr>
      </thead>
      <tbody>
        {sortedOrders.length === 0 ? (
          <tr>
            <td
              colSpan={storeTableColSpan}
              className="inventory-items-hint inventory-table-empty-cell"
            >
              No {isHistoryView ? 'order history' : 'orders'} found.
            </td>
          </tr>
        ) : (
          sortedOrders.map((order) => {
            const id = orderKey(order);
            const label = order.displayOrderRef ?? order.display_order_ref ?? id;
            const lineItems = getFoodItems(order);
            const nextStep = !isHistoryView
              ? getNextStoreOrderStep(storeChannel, order)
              : null;
            const statusChannel = storeOrderStatusChannel(storeChannel, order);
            const rawStatus = order.orderStatusCode || order.OrderStatus || order.orderStatus;
            const name = resolveStoreParty(order, isRestaurantChannel);
            return (
              <tr
                key={id ?? label}
                className="eats-admin-row--clickable"
                onClick={rowClickHandler(() => openOrderDetail(order))}
              >
                {renderNameCell(order, name, label, rawStatus)}
                <td className="store-orders-items-cell">
                  <OrderItemsSummary
                    items={lineItems}
                    onClick={() => openOrderDetail(order)}
                    emptyText="—"
                  />
                </td>
                <td className="purchase-amount-cell">
                  {formatMoney(order.TotalPrice ?? order.totalPrice ?? order.grandTotal)}
                </td>
                <td className="orders-status-cell">
                  <OrderStatusPill status={rawStatus} channel={statusChannel} />
                </td>
                <td>{order.orderType || '—'}</td>
                <td>{formatDate(order.CreatedDate ?? order.createdDate)}</td>
                {renderActionsCell(order, name, nextStep)}
              </tr>
            );
          })
        )}
      </tbody>
    </table>
  );

  const renderRestaurantTable = () => (
    <table
      className={`menu-table today-order-table orders-name-table${
        isJreats ? ' today-order-table--jreats' : ' today-order-table--online'
      }`}
    >
                <thead>
                    <tr>
          <OrdersSortableTh
            label="Name"
            sortKey="orderId"
            activeSortKey={sort?.key}
            sortDirection={sort?.direction}
            onSortAsc={handleSortAsc}
            onSortDesc={handleSortDesc}
          />
          {isJreats ? (
            <>
              <th>Meal Type</th>
              <OrdersSortableTh
                label="Food Items"
                sortKey="items"
                activeSortKey={sort?.key}
                sortDirection={sort?.direction}
                onSortAsc={handleSortAsc}
                onSortDesc={handleSortDesc}
              />
              <OrdersSortableTh
                label="Total ₹"
                sortKey="total"
                activeSortKey={sort?.key}
                sortDirection={sort?.direction}
                onSortAsc={handleSortAsc}
                onSortDesc={handleSortDesc}
              />
              <th>Billing</th>
              <OrdersSortableTh
                label="School"
                sortKey="organization"
                activeSortKey={sort?.key}
                sortDirection={sort?.direction}
                onSortAsc={handleSortAsc}
                onSortDesc={handleSortDesc}
              />
              <th>Class</th>
              <th>Section</th>
              <th>Roll No</th>
              <OrdersSortableTh
                label="Receiver"
                sortKey="receiver"
                activeSortKey={sort?.key}
                sortDirection={sort?.direction}
                onSortAsc={handleSortAsc}
                onSortDesc={handleSortDesc}
              />
              <OrdersSortableTh
                label="Status"
                sortKey="status"
                activeSortKey={sort?.key}
                sortDirection={sort?.direction}
                onSortAsc={handleSortAsc}
                onSortDesc={handleSortDesc}
              />
              <OrdersSortableTh
                label="Created On"
                sortKey="createdDate"
                activeSortKey={sort?.key}
                sortDirection={sort?.direction}
                onSortAsc={handleSortAsc}
                onSortDesc={handleSortDesc}
              />
              <th className="menu-table-actions-head">Actions</th>
            </>
          ) : (
            <>
              <OrdersSortableTh
                label="Food Items"
                sortKey="items"
                activeSortKey={sort?.key}
                sortDirection={sort?.direction}
                onSortAsc={handleSortAsc}
                onSortDesc={handleSortDesc}
              />
              <OrdersSortableTh
                label="Total ₹"
                sortKey="total"
                activeSortKey={sort?.key}
                sortDirection={sort?.direction}
                onSortAsc={handleSortAsc}
                onSortDesc={handleSortDesc}
              />
            </>
          )}
          {!isJreats && (
            <>
          <OrdersSortableTh
            label="Order Status"
            sortKey="status"
            activeSortKey={sort?.key}
            sortDirection={sort?.direction}
            onSortAsc={handleSortAsc}
            onSortDesc={handleSortDesc}
          />
          <OrdersSortableTh
            label="Created On"
            sortKey="createdDate"
            activeSortKey={sort?.key}
            sortDirection={sort?.direction}
            onSortAsc={handleSortAsc}
            onSortDesc={handleSortDesc}
          />
            <th>Delivery Time</th>
          {showOrganizationColumn && (
            <OrdersSortableTh
              label="Organization Name"
              sortKey="organization"
              activeSortKey={sort?.key}
              sortDirection={sort?.direction}
              onSortAsc={handleSortAsc}
              onSortDesc={handleSortDesc}
            />
          )}
          <OrdersSortableTh
            label="Receiver"
            sortKey="receiver"
            activeSortKey={sort?.key}
            sortDirection={sort?.direction}
            onSortAsc={handleSortAsc}
            onSortDesc={handleSortDesc}
          />
          <th className="menu-table-actions-head">Actions</th>
            </>
          )}
                    </tr>
                </thead>
                <tbody>
        {sortedOrders.length === 0 ? (
          <tr>
            <td
              colSpan={restaurantTableColSpan}
              className="no-orders-message menu-table-empty-cell"
            >
              No {isHistoryView ? 'order history' : 'orders'} found.
                            </td>
                        </tr>
                    ) : (
          sortedOrders.map((order) => {
                            const id = orderKey(order);
            const label = order.displayOrderRef ?? order.display_order_ref ?? id;
            const lineItems = getFoodItems(order);
            const rawStatus = resolveOrderStatusRaw(order);
            const nextStep = !isHistoryView
              ? getNextOrderStep(restaurantChannel, rawStatus)
              : null;
            const name = resolveCustomerName(order);
                            return (
              <tr
                key={id ?? label}
                className="eats-admin-row--clickable"
                onClick={rowClickHandler(() => openOrderDetail(order))}
              >
                {renderNameCell(order, name, label, rawStatus)}
                {isJreats ? (
                  <>
                    <td>{resolveJreatsMealType(order) || '—'}</td>
                    <td>
                      <OrderItemsSummary
                        items={lineItems}
                        onClick={() => openOrderDetail(order)}
                        emptyText="—"
                      />
                    </td>
                    <td>{formatMoney(order.TotalPrice ?? order.totalPrice)}</td>
                    <td>
                      <OrderBillingCell order={order} />
                    </td>
                    <td>{order.SchoolName || order.organizationName || '—'}</td>
                    <td>{order.className ?? order.ClassName ?? '—'}</td>
                    <td>{order.section ?? order.Section ?? '—'}</td>
                    <td>{order.rollNumber ?? order.RollNumber ?? '—'}</td>
                    <td>{order.ReceiverName || order.receiverName || '—'}</td>
                    <td className="orders-status-cell">
                      <OrderStatusPill status={rawStatus} />
                    </td>
                    <td>{formatDate(order.CreatedDate ?? order.createdDate)}</td>
                    {renderActionsCell(order, name, nextStep)}
                  </>
                ) : (
                  <>
                                    <td>
                      <OrderItemsSummary
                        items={lineItems}
                        onClick={() => openOrderDetail(order)}
                        emptyText="—"
                      />
                    </td>
                    <td>{formatMoney(order.TotalPrice ?? order.totalPrice)}</td>
                  </>
                )}
                {!isJreats && (
                  <>
                <td className="orders-status-cell">
                  <OrderStatusPill status={rawStatus} />
                </td>
                <td>{formatDate(order.CreatedDate ?? order.createdDate)}</td>
                  <td>
                    {order.deliveryTime ??
                      order.DeliveryTime ??
                      order.Delivery_Time ??
                      '—'}
                  </td>
                {showOrganizationColumn && (
                  <td>{order.SchoolName || order.organizationName || '—'}</td>
                )}
                <td>{order.ReceiverName || order.receiverName || '—'}</td>
                {renderActionsCell(order, name, nextStep)}
                  </>
                )}
                                        </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
  );

  return (
    <div
      className={`menu orders-page delivery-agent-orders-page${
        isStore ? ' store-orders-page' : ''
      }`}
    >
      <div className="today-order-toolbar today-order-toolbar--dine-in store-orders-toolbar">
        <div className="today-order-toolbar__row">
          <div className="today-order-toolbar__main">
            <CollapsibleFilters>
            <ToolbarRichSelect
              panelId="delivery-agent-order-view-filter"
              value={normalizeOrderView(orderView)}
              onChange={(v) => setOrderView(normalizeOrderView(v))}
              options={ORDER_VIEW_OPTIONS}
              ariaLabel="Active orders or order history"
              panelTitle="Order view"
            />
            <OrderStatusFilterDropdown
              panelId={
                isHistoryView
                  ? 'delivery-agent-history-status-filter'
                  : 'delivery-agent-active-status-filter'
              }
              statusOptions={statusFilterOpts}
              statusSelections={statusSelections}
              onToggleStatus={toggleStatusFilter}
            />
            {isHistoryView ? (
              <OrderHistoryDateFilters
                fromDate={historyFromDate}
                toDate={historyToDate}
                onFromDateChange={setHistoryFromDate}
                onToDateChange={setHistoryToDate}
              />
            ) : null}
            </CollapsibleFilters>
            <button
              type="button"
              className="menu-toolbar-btn today-order-toolbar__refresh"
              onClick={loadOrders}
              disabled={loading}
              aria-label="Refresh orders"
              title="Refresh"
            >
              <FaSyncAlt className={loading ? 'is-spinning' : ''} aria-hidden />
            </button>
          </div>
        </div>
      </div>

      {loading ? <p className="inventory-items-hint">Loading orders…</p> : null}

      {!loading ? (
        <OrdersTablePanel
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search table…"
          searchAriaLabel="Search delivery agent orders"
          resultCount={sortedOrders.length}
          className={
            isStore
              ? 'today-order-table-container--dine-in store-orders-table-panel'
              : 'today-order-table-container'
          }
          tableWrapperClassName={
            isStore
              ? 'order-history-table-container store-orders-table-wrapper'
              : 'today-order-table-container'
          }
        >
          {isStore ? renderStoreTable() : renderRestaurantTable()}
        </OrdersTablePanel>
      ) : null}
        </div>
    );
};

export default DeliveryAgent;
